import { Add, Close, Delete, Edit, ExpandMore, History, MoreVert, PushPin, PushPinOutlined, ReceiptLong, Visibility } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tabs,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AddCustomerModalV4 from '../../common/AddCustomerModalV4';
import CommonDialog from '../../common/CommonDialog';
import { firstLinked, getInvoiceDetailUrl, getInvoiceSyncFromWorkOrderUrl, workOrderInvoiceToSync } from '../../common/LinkedDocumentLink';
import { USER_TYPES } from '../../config/dataService/constants';
import SyncUpdateIcon from '../../utility/Svg/SyncUpdateIcon';
import { useDeleteWorkOrder, useMarkWorkOrderAuthorized, useSendWorkOrderEmail, useWorkOrderList } from '../../services/business/workOrders';
import useWorkOrderStore from '../../store/useWorkOrderStore';
import { normalizeWorkOrderStatus, USER_TIMEZONE, WORK_ORDER_STATUS, WORK_ORDER_STATUS_COLOR, WORK_ORDER_STATUS_LABEL } from '../../utility/Constant';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import EmptyWorkOrderSvg from '../../utility/Svg/EmptyWorkOrderSvg';
import NoSearchResultSvg from '../../utility/Svg/NoSearchResultSvg';
import ResendSvg from '../../utility/Svg/ResendSvg';
import ManageColumnsMenu from '../modal/Managecolumnsmenu.jsx';
import SelectVehicleModal from '../modal/SelectVehicleModal';
import CancelWorkOrderModal from './WorkOrderCommonComponents/CancelWorkOrderModal';
import WorkOrderHistoryModal from './WorkOrderCommonComponents/WorkOrderHistoryModal';

const styles = {
    menuPadding: {
        padding: '0',
        width: 'calc(100% + 10px)!important',
    },
    formControlLabel: {
        padding: '6px 16px',
        marginRight: '0!important',
        width: '100%',
    },
};

/** Tab index ⇄ url segment ⇄ list status filter. */
const WORK_ORDER_TABS = [
    { label: 'ALL', segment: 'all', status: '' },
    { label: 'Open', segment: 'open', status: WORK_ORDER_STATUS.OPEN },
    { label: 'Authorized', segment: 'authorized', status: WORK_ORDER_STATUS.AUTHORIZED },
    { label: 'Reapprove', segment: 'reapproval', status: WORK_ORDER_STATUS.REAPPROVAL },
    { label: 'Invoiced', segment: 'invoiced', status: WORK_ORDER_STATUS.INVOICED },
    { label: 'Drafts', segment: 'drafts', status: WORK_ORDER_STATUS.DRAFT },
    { label: 'Cancelled', segment: 'cancelled', status: WORK_ORDER_STATUS.CANCELLED },
];

const STATUS_FILTER_OPTIONS = [
    { _id: WORK_ORDER_STATUS.OPEN, label: 'Open' },
    { _id: WORK_ORDER_STATUS.AUTHORIZED, label: 'Authorized' },
    { _id: WORK_ORDER_STATUS.REAPPROVAL, label: 'Reapprove' },
    { _id: WORK_ORDER_STATUS.INVOICED, label: 'Invoiced' },
    { _id: WORK_ORDER_STATUS.DRAFT, label: 'Draft' },
    { _id: WORK_ORDER_STATUS.CANCELLED, label: 'Cancelled' },
];

/** The only `sortBy` values the list endpoint accepts. */
const WORK_ORDER_SORT_KEYS = {
    NUMBER: 'workOrderNumber',
    DATE: 'workOrderDate',
    CUSTOMER: 'customer.fullName',
    TOTAL: 'total',
};

const WORK_ORDER_LIST_TABLE_SX = { tableLayout: 'auto', width: '100%' };

/**
 * MUI only reveals the sort arrow on the active column (or on hover), which leaves the
 * other sortable columns looking unsortable. Keep every arrow visible, dimmed until active.
 */
const sortLabelSx = (isActive) => ({
    fontWeight: 500,
    '& .MuiTableSortLabel-icon': {
        opacity: isActive ? 1 : 0.4,
    },
});
const EMPTY_CELL_SX = { py: 6, borderBottom: 'none' };
const EMPTY_TITLE_SX = { marginTop: '20px', fontSize: '42px' };
const EMPTY_SUBTITLE_SX = {
    color: 'rgba(0, 0, 0, 0.60)',
    maxWidth: 536,
    fontSize: '18px',
    fontWeight: 400,
    letterSpacing: '0.17px',
    lineHeight: '143%',
    textAlign: 'center',
    mt: '20px !important',
    mb: '4px !important',
};
const WORK_ORDER_LIST_ACTION_COL = { width: 48, minWidth: 48, maxWidth: 48 };
const WORK_ORDER_LIST_COL = {};
const WORK_ORDER_LIST_BODY_CELL = { whiteSpace: 'nowrap' };

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`work-order-tabpanel-${index}`} aria-labelledby={`work-order-tab-${index}`} {...other}>
            {value === index && <Box sx={{ pt: 4 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `work-order-tab-${index}`,
        'aria-controls': `work-order-tabpanel-${index}`,
    };
}

export function formatWorkOrderVehicleDisplay(vehicle) {
    if (!vehicle || Object.keys(vehicle).length === 0) return '—';
    const year = vehicle.year != null && vehicle.year !== '' ? String(vehicle.year) : '';
    const make = vehicle.vehicleMake?.name ?? '';
    const model = vehicle.vehicleModel?.name ?? '';
    const parts = [year, make, model].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
}

export function formatWorkOrderLicensePlateDisplay(vehicle) {
    if (!vehicle?.licenseNumber) return '—';
    return vehicle?.provinceCode ? `${vehicle.provinceCode} ${vehicle.licenseNumber}` : String(vehicle.licenseNumber);
}

/**
 * Status chip used by the listing and the detail header.
 * `status` must already be normalised (see normalizeWorkOrderStatus) — an unmapped value
 * still renders, title-cased, rather than disappearing.
 */
export const WorkOrderStatusChip = ({ status, size, height = '40px', fontsize = '16px' }) => {
    const palette = WORK_ORDER_STATUS_COLOR[status] || WORK_ORDER_STATUS_COLOR[WORK_ORDER_STATUS.OPEN];
    const label = WORK_ORDER_STATUS_LABEL[status] || (status ? String(status).charAt(0).toUpperCase() + String(status).slice(1) : '—');

    return (
        <Chip
            label={label}
            size={size}
            style={{
                backgroundColor: palette.bg,
                color: palette.color,
                height: height,
                fontSize: fontsize,
                fontWeight: 500,
                lineHeight: '150%',
                letterSpacing: '0.15px',
                borderRadius: '100px',
                width: 'max-content',
            }}
        />
    );
};

/** Shown when the shop has no work orders at all. */
const EmptyState = ({ navigate }) => (
    <Stack alignItems="center" spacing={2}>
        <EmptyWorkOrderSvg />
        <Typography variant="h4" fontWeight={500} style={EMPTY_TITLE_SX}>
            No work orders yet
        </Typography>
        <Typography variant="h6" sx={EMPTY_SUBTITLE_SX}>
            Create a work order to list authorized repairs, print it for the customer to sign, and convert it into an invoice when the work is complete.
        </Typography>
        <Button variant="outlined" color="primary" startIcon={<Add />} onClick={() => navigate('/work-orders/new')}>
            NEW WORK ORDER
        </Button>
    </Stack>
);

/** Shown when filters are applied but nothing matches. */
const EmptyResult = () => (
    <Stack alignItems="center" spacing={2}>
        <NoSearchResultSvg />
        <Typography variant="h4" fontWeight={500} style={EMPTY_TITLE_SX}>
            No Results Found
        </Typography>
        <Typography variant="h6" sx={EMPTY_SUBTITLE_SX}>
            Please clear filters and try again.
        </Typography>
    </Stack>
);

const WorkOrders = () => {
    const {
        isPinned,
        setPinned,
        workOrderStatus,
        setWorkOrderStatus,
        resetWorkOrderStore,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        search,
        setSearch,
        tabValue: value,
        setTabValue: setValue,
        dateData,
        setDateData,
        selectDate,
        setSelectDate,
        yearData,
        setYearData,
        makeData,
        setMakeData,
        modelData,
        setModelData,
        licenseNumber,
        setLicenseNumber,
        fleetUnitNumber,
        setFleetUnitNumber,
        provinceId,
        setProvinceId,
        vinFilter,
        setVinFilter,
        sortBy,
        setSortBy,
        sortType,
        setSortType,
        showWorkOrderLicensePlateColumn,
        setShowWorkOrderLicensePlateColumn,
        showWorkOrderUnitColumn,
        setShowWorkOrderUnitColumn,
    } = useWorkOrderStore();

    const navigate = useNavigate();
    const location = useLocation();

    const nameArray = location.pathname.split('/');
    const name = nameArray[nameArray.length - 1];

    // Reset the stored filters when the module is entered from outside
    useEffect(() => {
        const prevPath = sessionStorage.getItem('prevPath');
        const isInternal = prevPath && prevPath.startsWith('/work-orders');
        if (!isInternal) {
            resetWorkOrderStore();
        }
        sessionStorage.setItem('prevPath', location.pathname);
    }, [location.pathname]);

    // Keep the active tab in sync with the url segment
    useEffect(() => {
        const tabIndex = WORK_ORDER_TABS.findIndex((tab) => tab.segment === name);
        setValue(tabIndex >= 0 ? tabIndex : 0);
    }, [name]);

    const [anchorElStatus, setAnchorElStatus] = useState(null);
    const [anchorElDropdown, setAnchorElDropdown] = useState(null);
    const [workOrderColumnAnchorEl, setWorkOrderColumnAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState({});
    const [openVehicleModal, setOpenVehicleModal] = useState(false);
    /** The list endpoint filters by a single vehicle id (`vehicleId`), set by the vehicle modal. */
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [openHistoryModal, setOpenHistoryModal] = useState(false);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openEditCustomer, setOpenEditCustomer] = useState(false);

    const openStatus = Boolean(anchorElStatus);
    const openDropdown = Boolean(anchorElDropdown);
    const openWorkOrderColumnSettings = Boolean(workOrderColumnAnchorEl);

    const currentPageStatus = WORK_ORDER_TABS[value]?.status ?? '';

    /**
     * GET work-order-authorization/list
     * The status filter menu wins over the tab when the user has picked one.
     */
    const listParams = {
        pagination: true,
        limit: rowsPerPage,
        page,
        status: workOrderStatus?.length ? workOrderStatus.join(',') : currentPageStatus,
        date: dateData,
        sortBy,
        sortType,
        timezone: USER_TIMEZONE,
        search,
        // Vehicle filter — the same field names the invoice listing sends. The modal returns these
        // seven values, never a vehicle id, which is why filtering by vehicle did nothing before.
        vehicleMakeId: makeData,
        vehicleModelId: modelData,
        vehicleYear: yearData,
        licenseNumber,
        fleetUnitNumber,
        provinceId,
        vin: vinFilter,
    };

    const { data: workOrderData, isPending, isFetching } = useWorkOrderList(listParams);
    // Show the loader for any fetch — first load, sorting, filtering or paging
    const showLoader = isPending || isFetching;
    const deleteWorkOrder = useDeleteWorkOrder();
    const sendWorkOrderEmail = useSendWorkOrderEmail();

    const handleSend = async (workOrderId) => {
        const idToSend = workOrderId || selectedRow?._id;
        if (!idToSend) return;
        try {
            await sendWorkOrderEmail.mutateAsync(idToSend);
            toast.success('Work order was successfully emailed to customer');
        } catch (error) {
            console.error('Error sending work order email:', error);
        }
    };

    const vehicleFieldCount = useMemo(
        () => [yearData, makeData, modelData, licenseNumber, fleetUnitNumber, provinceId, vinFilter].filter(Boolean).length,
        [yearData, makeData, modelData, licenseNumber, fleetUnitNumber, provinceId, vinFilter]
    );

    const hasClearableFilters = Boolean(dateData || search || vehicleFieldCount > 0 || (!isPinned && workOrderStatus?.length > 0));
    const clearFilterLabel = 'CLEAR FILTERS';

    /**
     * Search is debounced the way the invoice listing debounces it: the field updates on every
     * keystroke, the query only refetches once typing pauses.
     */
    const [searchInput, setSearchInput] = useState(search);
    const searchDebounceRef = useRef(null);

    // Keep the field in step when the store's value changes elsewhere (Clear Filters, tab change).
    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

    const handleSearchChange = (value) => {
        setSearchInput(value);

        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setSearch(value);
            setPage(1);
        }, 800);
    };

    const handleMenuOpen = (event, row) => {
        setAnchorElDropdown(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => setAnchorElDropdown(null);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setPage(1);
        navigate(`/work-orders/${WORK_ORDER_TABS[newValue].segment}`);
    };

    const handleDateChange = (newValue) => {
        setSelectDate(newValue);
        setDateData(newValue ? dayjs(newValue).format('YYYY-MM-DD') : '');
        setPage(1);
    };

    /** Clicking a column sorts by it; clicking the active column flips the direction. */
    const handleSort = (key) => {
        setSortType(sortBy === key && sortType === 'ASC' ? 'DSC' : 'ASC');
        setSortBy(key);
        setPage(1);
    };

    const handleChangePage = (event, newPage) => setPage(newPage + 1);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleVehicleApply = (next) => {
        setYearData(next?.yearData ?? '');
        setMakeData(next?.makeData ?? '');
        setModelData(next?.modelData ?? '');
        setLicenseNumber(next?.licenseNumber ?? '');
        setFleetUnitNumber(next?.fleetUnitNumber ?? '');
        setProvinceId(next?.provinceId ?? '');
        setVinFilter(next?.vinFilter ?? '');
        setPage(1);
        setOpenVehicleModal(false);
    };

    const handleClearFilters = () => {
        setSelectDate(null);
        setDateData('');
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setSearch('');
        if (!isPinned) {
            setWorkOrderStatus([]);
        }
        setPage(1);
    };

    const handleDelete = async () => {
        try {
            await deleteWorkOrder.mutateAsync(selectedRow?._id);
            toast.success('Work order was successfully deleted');
        } catch (error) {
            console.error('Error deleting work order:', error);
        } finally {
            setIsDeleteModal(false);
        }
    };

    /** Convert To Invoice — Open / Reapprove are authorized first (spec §4.4); Draft is refused. */
    const markAuthorized = useMarkWorkOrderAuthorized();
    const [isAuthorizingForConvert, setIsAuthorizingForConvert] = useState(false);

    /**
     * Open / Reapprove are authorized on the way into the conversion (client: save the extra click),
     * with the page loader up; an Authorized work order goes straight through.
     */
    const goToInvoiceConversion = async (row) => {
        const rowStatus = normalizeWorkOrderStatus(row?.status);
        if (rowStatus === WORK_ORDER_STATUS.OPEN || rowStatus === WORK_ORDER_STATUS.REAPPROVAL) {
            setIsAuthorizingForConvert(true);
            try {
                await markAuthorized.mutateAsync(row?._id);
                toast.success('Work order status updated successfully');
            } catch (error) {
                console.error('Error authorizing work order before conversion:', error);
                setIsAuthorizingForConvert(false);
                return;
            }
            setIsAuthorizingForConvert(false);
        }
        navigate('/invoice/new-invoice-from-work-order', { state: { workOrderData: row } });
    };

    const handleConvertToInvoice = () => {
        const rowStatus = normalizeWorkOrderStatus(selectedRow?.status);
        const canConvert = [WORK_ORDER_STATUS.AUTHORIZED, WORK_ORDER_STATUS.OPEN, WORK_ORDER_STATUS.REAPPROVAL].includes(rowStatus);
        handleMenuClose();
        if (!canConvert) {
            toast.error('Only authorized work orders can be converted to an invoice.');
            return;
        }
        goToInvoiceConversion(selectedRow);
    };

    const rows = Array.isArray(workOrderData?.data) ? workOrderData.data : [];

    /** Header and pagination are hidden only when the shop has nothing at all — not when a filter simply matched nothing. */
    const isEmptyWithoutFilters = rows.length === 0 && !showLoader && !hasClearableFilters;

    /** colSpan for the loader / empty rows: 6 fixed columns + optional columns + the actions column. */
    const columnCount = 7 + (showWorkOrderLicensePlateColumn ? 1 : 0) + (showWorkOrderUnitColumn ? 1 : 0);

    const openWorkOrder = (row) => navigate(`/work-orders/${normalizeWorkOrderStatus(row?.status)}/${row?._id}`);

    return (
        <>
            <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
                <Grid container rowGap={3}>
                    <Grid container item justifyContent={'space-between'}>
                        <Grid item sm={4} lg={4}>
                            <Typography variant="h4" sx={{ fontWeight: 400 }}>
                                Work Orders
                            </Typography>
                        </Grid>
                        <Grid container sm={8} lg={8} gap={3} justifyContent={'end'}>
                            <Grid>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    onClick={() => navigate('/work-orders/new')}
                                    className="btn-text-size-manage"
                                >
                                    NEW WORK ORDER
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid container item margin={'12px 0'} alignItems={'center'} justifyContent={'space-between'}>
                        <Grid item container alignItems={'center'}>
                            <Box sx={{ width: '100%' }}>
                                <Box>
                                    <Tabs value={value} onChange={handleChange} aria-label="work order tabs">
                                        {WORK_ORDER_TABS.map((tab, index) => (
                                            <Tab key={tab.segment} label={tab.label} {...a11yProps(index)} style={{ fontWeight: 500 }} />
                                        ))}
                                    </Tabs>
                                </Box>

                                {WORK_ORDER_TABS.map((tab, i) => (
                                    <CustomTabPanel value={value} key={tab.segment} index={i}>
                                        <Grid container item>
                                            <Grid container marginBottom={'32px'} alignItems={'center'} justifyContent={'space-between'}>
                                                <Grid container xs={12} sm={6} md={8} justifyContent={'flex-start'} alignItems={'center'}>
                                                    {/* Select Date */}
                                                    <Grid item xs="auto" padding={'6px'}>
                                                        <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                <DatePicker
                                                                    label="Select date"
                                                                    value={selectDate}
                                                                    onChange={handleDateChange}
                                                                    sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            sx: {
                                                                                width: 240,
                                                                                minWidth: 240,
                                                                                maxWidth: 240,
                                                                                '& .MuiInputBase-root': { width: '100%' },
                                                                            },
                                                                            inputProps: { id: 'work-order-date-input' },
                                                                        },
                                                                        field: { clearable: true },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Stack>
                                                    </Grid>

                                                    {/* Filter by vehicle */}
                                                    <Grid item xs="auto" padding={'6px'}>
                                                        <Button
                                                            variant={vehicleFieldCount > 0 ? 'contained' : 'text'}
                                                            color="primary"
                                                            onClick={() => setOpenVehicleModal(true)}
                                                            sx={{
                                                                color: vehicleFieldCount > 0 ? 'white!important' : 'black!important',
                                                                fontWeight: 500,
                                                                lineHeight: '24px',
                                                                padding: '6px 12px!important',
                                                                height: '40px',
                                                            }}
                                                            startIcon={
                                                                vehicleFieldCount > 0 && (
                                                                    <Avatar
                                                                        sx={{
                                                                            bgcolor: 'white',
                                                                            color: 'primary.main',
                                                                            fontSize: '14px!important',
                                                                            fontWeight: '500!important',
                                                                            letterSpacing: '0.4px',
                                                                            lineHeight: '24px',
                                                                            width: 24,
                                                                            height: 24,
                                                                        }}
                                                                    >
                                                                        {vehicleFieldCount}
                                                                    </Avatar>
                                                                )
                                                            }
                                                            endIcon={<ExpandMoreIcon />}
                                                        >
                                                            FILTER BY VEHICLE
                                                        </Button>
                                                        <SelectVehicleModal
                                                            open={openVehicleModal}
                                                            onCancel={() => setOpenVehicleModal(false)}
                                                            onApply={handleVehicleApply}
                                                            yearData={yearData}
                                                            makeData={makeData}
                                                            modelData={modelData}
                                                            licenseNumber={licenseNumber}
                                                            fleetUnitNumber={fleetUnitNumber}
                                                            provinceId={provinceId}
                                                            vinFilter={vinFilter}
                                                        />
                                                    </Grid>

                                                    {/* Status filter */}
                                                    <Grid item xs="auto" padding={'6px'}>
                                                        <Button
                                                            variant={(workOrderStatus?.length ?? 0) > 0 || isPinned ? 'contained' : 'text'}
                                                            id="work-order-status-filter-button"
                                                            aria-haspopup="true"
                                                            onClick={(event) => setAnchorElStatus(event.currentTarget)}
                                                            sx={{
                                                                color: (workOrderStatus?.length ?? 0) > 0 || isPinned ? 'white!important' : 'black!important',
                                                                fontWeight: 500,
                                                                lineHeight: '24px',
                                                                padding: '6px 12px!important',
                                                                height: '40px',
                                                            }}
                                                            startIcon={
                                                                workOrderStatus?.length > 0 && (
                                                                    <Avatar
                                                                        sx={{
                                                                            bgcolor: 'white',
                                                                            color: 'primary.main',
                                                                            fontSize: '14px!important',
                                                                            fontWeight: '500!important',
                                                                            letterSpacing: '0.4px',
                                                                            lineHeight: '24px',
                                                                            width: 24,
                                                                            height: 24,
                                                                        }}
                                                                    >
                                                                        {workOrderStatus?.length}
                                                                    </Avatar>
                                                                )
                                                            }
                                                            endIcon={
                                                                <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                    <ExpandMore />
                                                                    {(workOrderStatus?.length > 0 || isPinned) && (
                                                                        <IconButton
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                setPinned(!isPinned);
                                                                            }}
                                                                            sx={{
                                                                                color:
                                                                                    (workOrderStatus?.length ?? 0) > 0 || isPinned
                                                                                        ? 'white'
                                                                                        : 'rgba(0,0,0,0.54)',
                                                                                width: '26px',
                                                                                height: '26px',
                                                                                background: isPinned ? '#003267' : 'rgba(0,0,0,0.20)',
                                                                            }}
                                                                        >
                                                                            {isPinned ? (
                                                                                <PushPin sx={{ transform: 'revert', fontSize: 16 }} />
                                                                            ) : (
                                                                                <PushPinOutlined sx={{ transform: 'rotate(45deg)', fontSize: 16 }} />
                                                                            )}
                                                                        </IconButton>
                                                                    )}
                                                                </Stack>
                                                            }
                                                        >
                                                            STATUS
                                                        </Button>

                                                        <Menu
                                                            id="work-order-status-menu"
                                                            anchorEl={anchorElStatus}
                                                            open={openStatus}
                                                            onClose={() => setAnchorElStatus(null)}
                                                            MenuListProps={{ 'aria-labelledby': 'work-order-status-filter-button' }}
                                                            PaperProps={{ style: { maxHeight: '400px' } }}
                                                        >
                                                            {STATUS_FILTER_OPTIONS.map((status) => (
                                                                <MenuItem key={status._id} sx={styles.menuPadding}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={workOrderStatus?.includes(status._id) || false}
                                                                                onChange={() => {
                                                                                    const current = workOrderStatus || [];
                                                                                    const updated = current.includes(status._id)
                                                                                        ? current.filter((id) => id !== status._id)
                                                                                        : [...current, status._id];
                                                                                    setWorkOrderStatus(updated);
                                                                                    setPage(1);
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={status.label}
                                                                        sx={styles.formControlLabel}
                                                                    />
                                                                </MenuItem>
                                                            ))}
                                                        </Menu>
                                                    </Grid>

                                                    {hasClearableFilters && (
                                                        <Grid item xs="auto" padding={'6px'}>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                startIcon={<Close />}
                                                                onClick={handleClearFilters}
                                                                sx={{
                                                                    background: '#0000001A',
                                                                    color: '#000000',
                                                                    '&:hover': {
                                                                        background: '#0000001A',
                                                                        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                    },
                                                                    boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                                                    fontWeight: 500,
                                                                    lineHeight: '24px',
                                                                }}
                                                            >
                                                                {clearFilterLabel}
                                                            </Button>
                                                        </Grid>
                                                    )}
                                                </Grid>

                                                {/* Search */}
                                                <Grid container xs={12} sm={4} md={4} justifyContent={'flex-end'} padding={'6px'}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="work-order-search">Search</InputLabel>
                                                        <OutlinedInput
                                                            id="work-order-search"
                                                            placeholder="Customer name, service, etc..."
                                                            startAdornment={
                                                                <InputAdornment position="start">
                                                                    <SearchIcon />
                                                                </InputAdornment>
                                                            }
                                                            value={searchInput}
                                                            label="Search"
                                                            onChange={(e) => handleSearchChange(e.target.value)}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>

                                            <Grid container item padding={'24px'} boxShadow={2} sx={{ bgcolor: 'white' }}>
                                                <Grid item xl={12} md={12} sm={12} xs={12}>
                                                    <TableContainer sx={{ maxHeight: 600, width: '100%' }}>
                                                        <Table stickyHeader aria-label="work orders table" sx={WORK_ORDER_LIST_TABLE_SX}>
                                                            {/* Header is hidden only when the shop has no work orders at all */}
                                                            {isEmptyWithoutFilters ? (
                                                                <></>
                                                            ) : (
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={sortBy === WORK_ORDER_SORT_KEYS.NUMBER}
                                                                                direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                                                onClick={() => handleSort(WORK_ORDER_SORT_KEYS.NUMBER)}
                                                                                sx={sortLabelSx(sortBy === WORK_ORDER_SORT_KEYS.NUMBER)}
                                                                            >
                                                                                WO Number
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={sortBy === WORK_ORDER_SORT_KEYS.DATE}
                                                                                direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                                                onClick={() => handleSort(WORK_ORDER_SORT_KEYS.DATE)}
                                                                                sx={sortLabelSx(sortBy === WORK_ORDER_SORT_KEYS.DATE)}
                                                                            >
                                                                                Date
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={sortBy === WORK_ORDER_SORT_KEYS.CUSTOMER}
                                                                                direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                                                onClick={() => handleSort(WORK_ORDER_SORT_KEYS.CUSTOMER)}
                                                                                sx={sortLabelSx(sortBy === WORK_ORDER_SORT_KEYS.CUSTOMER)}
                                                                            >
                                                                                Customer Name
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL} style={{ fontWeight: 500 }}>
                                                                            Vehicle
                                                                        </TableCell>
                                                                        {showWorkOrderLicensePlateColumn && (
                                                                            <TableCell align="left" sx={WORK_ORDER_LIST_COL} style={{ fontWeight: 500 }}>
                                                                                Licence Plate
                                                                            </TableCell>
                                                                        )}
                                                                        {showWorkOrderUnitColumn && (
                                                                            <TableCell align="left" sx={WORK_ORDER_LIST_COL} style={{ fontWeight: 500 }}>
                                                                                Unit #
                                                                            </TableCell>
                                                                        )}
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL}>
                                                                            <TableSortLabel
                                                                                active={sortBy === WORK_ORDER_SORT_KEYS.TOTAL}
                                                                                direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                                                onClick={() => handleSort(WORK_ORDER_SORT_KEYS.TOTAL)}
                                                                                sx={sortLabelSx(sortBy === WORK_ORDER_SORT_KEYS.TOTAL)}
                                                                            >
                                                                                Total
                                                                            </TableSortLabel>
                                                                        </TableCell>
                                                                        <TableCell align="left" sx={WORK_ORDER_LIST_COL} style={{ fontWeight: 500 }}>
                                                                            WO Status
                                                                        </TableCell>
                                                                        {/* The gear lives in the last column, directly above each row's menu button, and is
                                                                            laid out exactly like that button's cell — so the two line up and the table
                                                                            ends at the gear with no empty column after it. */}
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{ ...WORK_ORDER_LIST_BODY_CELL, ...WORK_ORDER_LIST_ACTION_COL }}
                                                                        >
                                                                            <Box display="flex" justifyContent="flex-end">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(event) => setWorkOrderColumnAnchorEl(event.currentTarget)}
                                                                                    aria-label="Manage columns"
                                                                                    // Same button the invoice listing uses: square, and boxed + shaded while its menu is open.
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        width: 32,
                                                                                        height: 32,
                                                                                        justifyContent: 'center',
                                                                                        alignItems: 'center',
                                                                                        flexShrink: 0,
                                                                                        aspectRatio: '1 / 1',
                                                                                        borderRadius: '4px',
                                                                                        border: openWorkOrderColumnSettings
                                                                                            ? '1px solid rgba(0, 0, 0, 0.10)'
                                                                                            : 'none',
                                                                                        backgroundColor: openWorkOrderColumnSettings
                                                                                            ? '#EBEBEB'
                                                                                            : 'transparent',
                                                                                        p: 0,
                                                                                        '&:hover': {
                                                                                            backgroundColor: openWorkOrderColumnSettings
                                                                                                ? '#EBEBEB'
                                                                                                : 'transparent',
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                                        <path
                                                                                            d="M12.4604 1.66699H7.53932V3.81826L5.96211 4.74505L4.12706 3.66859L1.6665 7.99873L3.5011 9.07491L3.50111 10.9258L1.66659 12.0019L4.12715 16.3321L5.96215 15.2557L7.53932 16.1824V18.3337H12.4604V16.1824L14.0376 15.2557L15.8725 16.3321L18.3331 12.0019L16.4987 10.9258V9.07482L18.3332 7.99873L15.8726 3.66859L14.0376 4.74502L12.4604 3.81826V1.66699Z"
                                                                                            stroke={!openWorkOrderColumnSettings ? '#8E8E8E' : 'black'}
                                                                                            strokeWidth="1.25"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                        <path
                                                                                            d="M12.9168 9.99967C12.9168 11.6105 11.611 12.9163 10.0002 12.9163C8.38933 12.9163 7.0835 11.6105 7.0835 9.99967C7.0835 8.38884 8.38933 7.08301 10.0002 7.08301C11.611 7.08301 12.9168 8.38884 12.9168 9.99967Z"
                                                                                            stroke={!openWorkOrderColumnSettings ? '#8E8E8E' : 'black'}
                                                                                            strokeWidth="1.25"
                                                                                        />
                                                                                    </svg>
                                                                                </IconButton>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                            )}

                                                            <TableBody>
                                                                {rows.length === 0 && !showLoader ? (
                                                                    <TableRow>
                                                                        <TableCell colSpan={columnCount} align="center" sx={EMPTY_CELL_SX}>
                                                                            {hasClearableFilters ? <EmptyResult /> : <EmptyState navigate={navigate} />}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ) : (
                                                                    <></>
                                                                )}

                                                                {rows.map((row) => (
                                                                    <TableRow key={row._id} sx={{ cursor: 'pointer' }} hover>
                                                                        <TableCell
                                                                            align="left"
                                                                            sx={WORK_ORDER_LIST_BODY_CELL}
                                                                            onClick={() => openWorkOrder(row)}
                                                                        >
                                                                            {row?.workOrderNumber || '—'}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            align="left"
                                                                            sx={WORK_ORDER_LIST_BODY_CELL}
                                                                            onClick={() => openWorkOrder(row)}
                                                                        >
                                                                            {/* Older records carry only createdAt */}
                                                                            {row?.workOrderDate || row?.createdAt
                                                                                ? dayjs(row?.workOrderDate || row?.createdAt)
                                                                                      .tz(USER_TIMEZONE)
                                                                                      .format('MMMM D, YYYY')
                                                                                : '—'}
                                                                        </TableCell>
                                                                        <TableCell align="left" onClick={() => openWorkOrder(row)}>
                                                                            {row?.customer?.fullName ||
                                                                                [row?.customer?.firstName, row?.customer?.lastName].filter(Boolean).join(' ') ||
                                                                                '—'}
                                                                        </TableCell>
                                                                        <TableCell align="left" onClick={() => openWorkOrder(row)}>
                                                                            {formatWorkOrderVehicleDisplay(row?.vehicle)}
                                                                        </TableCell>
                                                                        {showWorkOrderLicensePlateColumn && (
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={WORK_ORDER_LIST_BODY_CELL}
                                                                                onClick={() => openWorkOrder(row)}
                                                                            >
                                                                                {formatWorkOrderLicensePlateDisplay(row?.vehicle)}
                                                                            </TableCell>
                                                                        )}
                                                                        {showWorkOrderUnitColumn && (
                                                                            <TableCell
                                                                                align="left"
                                                                                sx={WORK_ORDER_LIST_BODY_CELL}
                                                                                onClick={() => openWorkOrder(row)}
                                                                            >
                                                                                {row?.vehicle?.fleetUnitNumber || row?.vehicle?.unitNumber || '—'}
                                                                            </TableCell>
                                                                        )}
                                                                        <TableCell
                                                                            align="left"
                                                                            sx={{ ...WORK_ORDER_LIST_BODY_CELL, fontWeight: 400 }}
                                                                            onClick={() => openWorkOrder(row)}
                                                                        >
                                                                            $
                                                                            {NumericEngine.format(row?.total ?? row?.Total ?? 0, {
                                                                                precision: 2,
                                                                                trailingZeros: true,
                                                                            })}
                                                                        </TableCell>
                                                                        <TableCell
                                                                            align="left"
                                                                            sx={WORK_ORDER_LIST_BODY_CELL}
                                                                            onClick={() => openWorkOrder(row)}
                                                                        >
                                                                            <WorkOrderStatusChip
                                                                                status={normalizeWorkOrderStatus(row?.status)}
                                                                                height="32px"
                                                                                fontsize="13px"
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{ ...WORK_ORDER_LIST_BODY_CELL, ...WORK_ORDER_LIST_ACTION_COL }}
                                                                        >
                                                                            <Box display="flex" justifyContent="flex-end">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    sx={{ background: '#EBEBEB', width: '24px', height: '24px' }}
                                                                                    onClick={(event) => {
                                                                                        event.stopPropagation();
                                                                                        handleMenuOpen(event, row);
                                                                                    }}
                                                                                >
                                                                                    <MoreVert sx={{ width: '18px', height: '18px' }} />
                                                                                </IconButton>
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>

                                                    {rows.length > 0 && (
                                                        <Grid container item alignItems={'center'} justifyContent="end">
                                                            <Grid item>
                                                                <TablePagination
                                                                    rowsPerPageOptions={[5, 10, 25]}
                                                                    colSpan={3}
                                                                    count={workOrderData?.totalCount || 0}
                                                                    rowsPerPage={rowsPerPage}
                                                                    page={page > 0 ? page - 1 : 0}
                                                                    slotProps={{
                                                                        select: { 'aria-label': 'rows per page' },
                                                                        actions: { showFirstButton: true, showLastButton: true },
                                                                    }}
                                                                    onPageChange={handleChangePage}
                                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </CustomTabPanel>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <ManageColumnsMenu
                anchorEl={workOrderColumnAnchorEl}
                open={openWorkOrderColumnSettings}
                onClose={() => setWorkOrderColumnAnchorEl(null)}
                columns={[
                    {
                        key: 'licensePlate',
                        label: 'Licence Plate',
                        checked: showWorkOrderLicensePlateColumn,
                        onToggle: setShowWorkOrderLicensePlateColumn,
                    },
                    { key: 'unit', label: 'Unit #', checked: showWorkOrderUnitColumn, onToggle: setShowWorkOrderUnitColumn },
                ]}
            />

            {/* Row actions — matches the Figma menu */}
            <Menu
                id="work-order-row-menu"
                anchorEl={anchorElDropdown}
                open={openDropdown}
                onClose={handleMenuClose}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '4px!important',
                        boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 5px 5px -3px rgba(0, 0, 0, 0.20)',
                    },
                    '& .MuiMenuItem-root': {
                        padding: '6px 12px!important',
                        // width: 'max-content',
                        // fontSize: '14px!important',
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        navigate(`/work-orders/edit/${selectedRow?._id}`);
                        handleMenuClose();
                    }}
                >
                    <Edit sx={{ fontSize: '20px' }} />
                    <span style={{ marginLeft: '10px' }}>Edit Work Order</span>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        if (!selectedRow) return;
                        navigate(`/customers/${selectedRow?.customer?._id}/${selectedRow?.customer?.createdFrom}`);
                        handleMenuClose();
                    }}
                >
                    <Visibility sx={{ fontSize: '20px' }} />
                    <span style={{ marginLeft: '10px' }}>View Customer</span>
                </MenuItem>

                {/* Resend — once emailed (not Draft / Cancelled), right after View Customer as on the detail page. */}
                {![WORK_ORDER_STATUS.DRAFT, WORK_ORDER_STATUS.CANCELLED].includes(normalizeWorkOrderStatus(selectedRow?.status)) &&
                    selectedRow?.isEmailSent && (
                        <MenuItem
                            onClick={() => {
                                const hasEmail = Boolean(selectedRow?.customer?.email);
                                handleMenuClose();
                                if (hasEmail) {
                                    handleSend(selectedRow?._id);
                                } else {
                                    setOpenEditCustomer(true);
                                }
                            }}
                        >
                            <div
                                style={{
                                    marginRight: '8px',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <ResendSvg />
                            </div>
                            <span style={{ marginLeft: '2px' }}>Resend</span>
                        </MenuItem>
                    )}

                {/*
                 * Linked invoice: Update To Invoice while it is behind the work order, View Invoice
                 * otherwise. No invoice: Convert To Invoice. A cancelled work order can only view one —
                 * with no invoice linked it offers nothing here.
                 */}
                {firstLinked(selectedRow?.linkedInvoiceId)?._id ? (
                    workOrderInvoiceToSync(selectedRow) && normalizeWorkOrderStatus(selectedRow?.status) !== WORK_ORDER_STATUS.CANCELLED ? (
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                navigate(getInvoiceSyncFromWorkOrderUrl(workOrderInvoiceToSync(selectedRow)._id), {
                                    state: { workOrderData: selectedRow },
                                });
                            }}
                        >
                            <SyncUpdateIcon />
                            <span style={{ marginLeft: '2px' }}>Update To Invoice</span>
                        </MenuItem>
                    ) : (
                        <MenuItem
                            onClick={() => {
                                handleMenuClose();
                                navigate(getInvoiceDetailUrl(firstLinked(selectedRow?.linkedInvoiceId)));
                            }}
                        >
                            <ReceiptLong sx={{ fontSize: '20px' }} />
                            <span style={{ marginLeft: '10px' }}>View Invoice</span>
                        </MenuItem>
                    )
                ) : normalizeWorkOrderStatus(selectedRow?.status) === WORK_ORDER_STATUS.CANCELLED ? null : (
                    <MenuItem onClick={handleConvertToInvoice}>
                        <ReceiptLong sx={{ fontSize: '20px' }} />
                        <span style={{ marginLeft: '10px' }}>Convert To Invoice</span>
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        setOpenHistoryModal(true);
                        handleMenuClose();
                    }}
                >
                    <History sx={{ fontSize: '20px' }} />
                    <span style={{ marginLeft: '10px' }}>History</span>
                </MenuItem>

                {/* Cancel is offered in every status except Draft, Invoiced and an already-cancelled one. */}
                {![WORK_ORDER_STATUS.CANCELLED, WORK_ORDER_STATUS.DRAFT, WORK_ORDER_STATUS.INVOICED].includes(
                    normalizeWorkOrderStatus(selectedRow?.status)
                ) && (
                    <MenuItem
                        onClick={() => {
                            setOpenCancelModal(true);
                            handleMenuClose();
                        }}
                    >
                        <Close sx={{ fontSize: '20px' }} />
                        <span style={{ marginLeft: '10px' }}>Cancel Work Order</span>
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        setIsDeleteModal(true);
                        handleMenuClose();
                    }}
                    sx={{ color: '#D32F2F' }}
                >
                    <Delete sx={{ color: '#D32F2F', fontSize: '20px' }} />
                    <span style={{ marginLeft: '10px' }}>Delete</span>
                </MenuItem>
            </Menu>

            <CommonDialog
                open={isDeleteModal}
                onClose={() => setIsDeleteModal(false)}
                onCancel={() => setIsDeleteModal(false)}
                onSave={handleDelete}
                loader={deleteWorkOrder.isPending}
                dialogTitle="Delete work order?"
                dialogSubtitle="This work order will be permanently deleted. This action cannot be undone."
                cancelButtonText="CANCEL"
                saveButtonText="DELETE"
            />

            <WorkOrderHistoryModal open={openHistoryModal} onClose={() => setOpenHistoryModal(false)} workOrderId={selectedRow?._id} />

            <CancelWorkOrderModal open={openCancelModal} onClose={() => setOpenCancelModal(false)} workOrder={selectedRow} />

            <Dialog fullScreen={false} open={showLoader || isAuthorizingForConvert}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            {openEditCustomer && (
                <Suspense fallback={<CircularProgress />}>
                    <AddCustomerModalV4
                        from="invoice_email"
                        openModal={openEditCustomer}
                        customerId={selectedRow?.customer?.refIdImport || selectedRow?.customer?.refIdUsers || selectedRow?.customer?._id}
                        customerType={selectedRow?.customer?.createdFrom || selectedRow?.customer?.source || USER_TYPES.SHOP_CREATED}
                        onClose={() => {
                            setOpenEditCustomer(false);
                        }}
                        onSubmitModal={(updatedCustomer) => {
                            setOpenEditCustomer(false);
                            handleSend(selectedRow?._id);
                        }}
                    />
                </Suspense>
            )}
        </>
    );
};

export default WorkOrders;
